(function initializeStickySummary(root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!root?.customElements || root.customElements.get('alvaura-sticky-summary')) return;

  const parse = (value) => {
    try { return JSON.parse(value); } catch { return null; }
  };
  const text = (element, value) => {
    if (!element) return;
    if (element.textContent !== value) element.textContent = value;
    if (element.hidden !== !value) element.hidden = !value;
  };

  root.customElements.define('alvaura-sticky-summary', class extends root.HTMLElement {
    connectedCallback() {
      this.sticky = this.closest('sticky-atc');
      if (!this.sticky?.hasAttribute('data-alvaura-sticky-summary-enabled')) return;
      this.payload = parse(this.querySelector('[data-sticky-summary-variants]')?.textContent);
      this.main = Array.from(root.document.querySelectorAll('product-info')).find((element) =>
        String(element.dataset.section) === String(this.dataset.sectionId) ||
        String(element.dataset.originalSection) === String(this.dataset.sectionId));
      if (!this.main || !this.payload) return;
      this.size = this.querySelector('[data-sticky-summary-size]');
      this.fulfillment = this.querySelector('[data-sticky-summary-fulfillment]');
      this.price = this.querySelector('[data-sticky-summary-price]');
      this.image = this.querySelector('.sticky-atc__media img');
      this.schedule = () => {
        if (this.frame) return;
        this.frame = root.requestAnimationFrame(() => {
          this.frame = null;
          if (this.isConnected) this.refresh();
        });
      };
      this.observer = new root.MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(this.main, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['data-ready', 'data-state', 'data-form-sync', 'data-form-variant-id', 'aria-busy', 'value', 'checked', 'selected'],
      });
      this.main.addEventListener('change', this.schedule);
      this.visibleRefresh = () => { if (!root.document.hidden) this.schedule(); };
      root.document.addEventListener('visibilitychange', this.visibleRefresh);
      this.timer = root.setInterval(this.visibleRefresh, 60000);
      this.refresh();
    }

    refresh() {
      const matches = Array.from(this.main.querySelectorAll('alvaura-availability'))
        .filter((element) => String(element.dataset.sectionId) === String(this.dataset.sectionId));
      const availability = matches.length === 1 ? matches[0] : null;
      const form = Array.from(this.main.querySelectorAll('form'))
        .find((element) => element.getAttribute('id') === this.sticky.getAttribute('form'));
      const input = form?.querySelector('input[name="id"].product-variant-id');
      const selectedOptions = Array.from(this.main.querySelectorAll('variant-selects'))
        .filter((picker) => !picker.closest('sticky-atc'))
        .flatMap((picker) => Array.from(picker.querySelectorAll('[data-alvaura-option-name]')))
        .map((group) => ({
          name: group.dataset.alvauraOptionName,
          value: group.querySelector('select')?.value || group.querySelector('input:checked')?.value || '',
        }));
      const selection = api.selectVariant(this.payload, {
        count: matches.length,
        ready: availability?.hasAttribute('data-ready') === true,
        busy: availability?.getAttribute('aria-busy') === 'true',
        formSync: availability?.dataset.formSync,
        syncedId: availability?.dataset.formVariantId,
        inputPresent: Boolean(input),
        inputId: input?.value,
        state: availability?.dataset.state,
        selectedOptions,
      });
      const variant = selection.variant;
      if (this.price && this.price.hidden !== !variant) this.price.hidden = !variant;
      text(this.size, variant ? selection.size : '');
      const message = availability?.querySelector('[data-availability-message]');
      const status = message?.querySelector('alvaura-fulfillment-copy strong')?.textContent?.trim()
        || message?.querySelector('strong')?.textContent?.trim() || '';
      const settings = parse(availability?.querySelector('[data-fulfillment-display-settings]')?.textContent);
      const delivery = variant ? api.fulfillmentLabel({
        now: new Date(), state: availability?.dataset.state,
        showDispatch: this.dataset.showDispatch === 'true', settings,
        status, presentation: root.AlvauraFulfillmentDisplay?.presentation,
      }) : { text: '', dispatch: false };
      text(this.fulfillment, delivery.text);
      const variantId = variant ? String(variant.id) : '';
      if (this.dataset.variantId !== variantId) this.dataset.variantId = variantId;
      const dispatch = delivery.dispatch ? 'displayed' : 'not_displayed';
      if (this.dataset.dispatch !== dispatch) this.dataset.dispatch = dispatch;
      if (variant?.image && this.image) {
        if (this.image.getAttribute('src') !== variant.image.src) this.image.setAttribute('src', variant.image.src);
        if (this.image.hasAttribute('srcset')) this.image.removeAttribute('srcset');
        if (Number(variant.image.width) > 0 && Number(variant.image.height) > 0) {
          this.image.setAttribute('width', String(variant.image.width));
          this.image.setAttribute('height', String(variant.image.height));
        }
        if (this.image.alt !== variant.image.alt) this.image.alt = variant.image.alt || '';
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.main?.removeEventListener('change', this.schedule);
      root.document.removeEventListener('visibilitychange', this.visibleRefresh);
      root.clearInterval(this.timer);
      if (this.frame) root.cancelAnimationFrame(this.frame);
    }
  });
})(typeof window === 'object' ? window : null, function createStickySummaryApi() {
  'use strict';
  const normalize = (value) => String(value ?? '').trim();

  function selectVariant(payload, source) {
    const unresolved = { variant: null, size: '' };
    if (!Array.isArray(payload?.variants) || !Array.isArray(payload?.optionNames) ||
      source.count !== 1 || !source.ready || source.busy || source.formSync !== 'ready' ||
      !source.inputPresent || source.state === 'pending' || source.state === 'unavailable') return unresolved;
    const inputId = normalize(source.inputId);
    const syncedId = normalize(source.syncedId);
    if (!/^\d+$/.test(syncedId) || (inputId && inputId !== syncedId)) return unresolved;
    const variants = payload.variants.filter((variant) => String(variant.id) === syncedId);
    if (variants.length !== 1) return unresolved;
    const variant = variants[0];
    const selections = source.selectedOptions || [];
    for (let index = 0; index < payload.optionNames.length; index++) {
      const name = payload.optionNames[index];
      const matching = selections.filter((selection) => normalize(selection.name) === normalize(name));
      if (matching.length > 1 || matching.some((selection) => normalize(selection.value) !== normalize(variant.options?.[index]))) return unresolved;
    }
    const sizeIndex = payload.optionNames.findIndex((name) => /^(size|length)$/i.test(normalize(name)));
    if (sizeIndex >= 0 && !selections.some((selection) => normalize(selection.name) === normalize(payload.optionNames[sizeIndex]) && normalize(selection.value))) return unresolved;
    const size = sizeIndex >= 0 ? `${payload.optionNames[sizeIndex]} ${variant.options[sizeIndex]}` : '';
    return { variant, size };
  }

  function fulfillmentLabel({ now, state, showDispatch, settings, status, presentation }) {
    const fallback = { text: status || '', dispatch: false };
    if (!showDispatch || !settings || typeof presentation !== 'function' || !['in_stock', 'made_to_order'].includes(state)) return fallback;
    const prefix = state === 'in_stock' ? 'stock' : 'mto';
    const dispatchSettings = { ...settings, timing_mode: 'dispatch', [`${prefix}_timing_mode`]: 'dispatch' };
    const result = presentation(now, state, dispatchSettings);
    if (!result?.label || result.warning) return fallback;
    return { text: result.label, dispatch: true };
  }

  return { selectVariant, fulfillmentLabel };
});
